import { Logger, LoggerFactory } from '../core/logger.service';
import { Injectable } from '@angular/core';
import { CampaignStatus, OnsiteCampaign } from './campaign.model';
import { BehaviorSubject } from 'rxjs';
import { BatchCreateCampaignConfiguration, BatchCreateCampaignsStatus } from './batch-create-campaigns.model';

@Injectable()
export class BatchCreateCampaignsService {
  public static readonly storageKey: string = 'batch-create-campaigns-data';
  public campaignCreationCompletedBS = new BehaviorSubject<OnsiteCampaign>(undefined);
  public campaignCreatedBS = new BehaviorSubject<OnsiteCampaign>(undefined);
  public static pollIntervalMs = 1000; // 1 sec
  public static maxPollTimeMs = 30000; // 30sec
  private readonly logger: Logger;
  private virtualCampaignId = -1;

  constructor(loggerFactory: LoggerFactory) {
    this.logger = loggerFactory.get('BatchCreateCampaignsService');
  }

  public updateStatus(
    batchCreateCampaignsWaitedMs: number,
    campaigns: OnsiteCampaign[],
    batchCampaignsToCreate: BatchCreateCampaignConfiguration[],
  ): BatchCreateCampaignsStatus {
    const status = {
      shouldWait: false,
      batchCampaignsToCreate: batchCampaignsToCreate,
    } as BatchCreateCampaignsStatus;

    if (batchCreateCampaignsWaitedMs > BatchCreateCampaignsService.maxPollTimeMs) {
      this.logger.debug(`Batch create campaigns: Aborted after ${batchCreateCampaignsWaitedMs} ms`);
      status.shouldWait = false; // Abort. Took to long
      return status;
    }

    if (campaigns.length === 0) {
      status.shouldWait = true; // No campaign created yet, create all as virtual
      return status;
    }

    for (const campaign of campaigns) {
      const isCreatedNow = !!status.batchCampaignsToCreate.find((x) => x.id === campaign.templateId);
      const isFullyCreated = this.isFullyCreated(campaign);
      if (isCreatedNow) {
        if (isFullyCreated) {
          this.logger.info(`Batch create campaigns fully created campaign`, campaign.id);
          status.batchCampaignsToCreate = status.batchCampaignsToCreate.filter((x) => x.id !== campaign.templateId); // Get rid of already created campaigns having preview image
          this.campaignCreationCompletedBS.next(campaign); // Inform BS-subscriptions about fully created campaign
        } else {
          this.logger.info(`Batch create campaigns partially created campaign`, campaign);
          this.campaignCreatedBS.next(campaign); //  Inform BS-subscriptions about creation that is not fully done yet
        }
      }
    }

    if (status.batchCampaignsToCreate.length === 0) {
      status.shouldWait = false; // All created and have preview images
      return status;
    }

    status.shouldWait = true; // Waiting for either more campaigns to be created or it's preview images
    return status;
  }

  public isVirtualCampaign(campaign: OnsiteCampaign) {
    return campaign.id === this.virtualCampaignId;
  }

  public isFullyCreated(campaign: OnsiteCampaign): boolean {
    // Referrals should have it's group to be fully created
    if (campaign.name?.endsWith('(Referrer)') && !campaign.referralCampaignGroup) {
      return false;
    }

    // All campaigns requires preview image to be fully created
    const hasPreivewImage = campaign?.widgets?.some((x) => x.states?.some((x) => !!x.previewImageUrl)) === true;
    if (hasPreivewImage) {
      return true;
    }

    return false;
  }

  public createVirtualCampaign(x: BatchCreateCampaignConfiguration): OnsiteCampaign {
    return {
      id: this.virtualCampaignId,
      status: CampaignStatus.Draft,
      templateId: x.id,
      name: x.templateName,
      hasInputFields: false,
      createdTime: new Date(),
      updatedTime: new Date(),
      scheduling: {},
      widgets: [],
    } as OnsiteCampaign;
  }

  public getCampaignsToShow(
    campaigns: OnsiteCampaign[],
    campaignsToCreate: BatchCreateCampaignConfiguration[],
  ): OnsiteCampaign[] {
    const campaignsToShow: OnsiteCampaign[] = campaigns; // All already created should be shown
    for (const campaignToCreate of campaignsToCreate) {
      // When campaign exists but is not fully done we need to avoid it from being added as virtual
      if (!campaignsToShow.find((x) => x.templateId == campaignToCreate.id)) {
        campaignsToShow.push(this.createVirtualCampaign(campaignToCreate));
      }
    }
    return campaignsToShow;
  }
}
